<template>
  <main class="bg-black c-white">
    <!-- Hero -->
    <section class="relative mb-16 flex justify-center">
      <div class="absolute full f-col uppercase md:w-4/5 md:justify-center <md:pt-16 <md:text-center">
        <h1 class="text-6xl fw-bold">
          {{ hero.title }}
        </h1>
        {{ hero.paragraphLine1 }} <br class="md:hidden"> {{ hero.paragraphLine2 }}
        <div class="<md:mx-a <md:w-max <md:f-col <md:px-10">
          <vf-button
            v-for="({ to, title }, key) in hero.buttons"
            :key
            class="mt-3 uppercase md:mr-3 md:mt-6"
            size="tiny"
            :to
            variant="primary-inverse"
          >
            {{ title }}
          </vf-button>
        </div>
      </div>
      <base-picture v-bind="hero.image" class="w-full" fit="cover" />
      <custom-arrow-scroll
        class="absolute bottom-0 left-0 mb-8 ml-8"
        size="md"
        to="#chooseYourDenali"
      />
    </section>
    <!-- Choose your Denali -->
    <section id="chooseYourDenali" class="text-center">
      <h1 class="text-4xl md:text-5xl">
        {{ chooseYourDenali.title }}
      </h1>
      <template v-for="({ title }, key) in chooseYourDenali.genders" :key>
        <button
          class="mx-4 <md:text-sm"
          :class="{ underlined: activeGender === key }"
          @click="activeGender = key"
        >
          {{ title }}
        </button>
        <span v-if="key < chooseYourDenali.genders.length - 1">|</span>
      </template>
      <br>
      <transition mode="out-in" name="fade">
        <base-link :key="activeGender" :to="chooseYourDenali.genders[activeGender].to">
          <base-picture class="md:w-1/2" v-bind="chooseYourDenali.genders[activeGender].image" />
        </base-link>
      </transition>
    </section>
    <!-- Stories Grid -->
    <!-- Columns are based on templates, in case of new stories they can be added within json file -->
    <section class="mb-16">
      <base-grid class="items-center" :cols="12">
        <div
          v-for="({
                    author,
                    authorRole,
                    characteristics,
                    images,
                    influencer,
                    mdGridTransformation,
                    paragraph,
                    renderStyle,
                    title,
                    year },
                  key) in stories"
          :key
          v-style="{
            'grid-col': { sm: 'span 12', md: 'span 4' },
            'grid-area': { md: mdGridTransformation },
          }"
          class="relative"
        >
          <!-- Hero -->
          <div
            v-if="renderStyle === 'hero'"
            class="absolute bottom-0 w-full bg-black bg-op-60 px-6 py-2"
          >
            <p class="text-2xl fw-bold">
              {{ year }}
            </p>
            <p class="text-sm fw-bold">
              {{ influencer }}
            </p>
          </div>
          <!-- Quote -->
          <div v-if="renderStyle === 'quote'" class="px-6 py-16 text-center text-sm">
            <div class="mb-6 ws-pre-wrap title-2 lh-4">
              {{ paragraph }}
            </div>
            <div class="mb-6 fw-bold">
              {{ author }}
            </div>
            <div>{{ authorRole }}</div>
          </div>
          <!-- Product Details -->
          <div v-if="renderStyle === 'productDetails'" class="absolute flex md:full md:items-center">
            <ul class="w-full bg-op-60 p-6 text-sm lh-4 <md:bg-black">
              <li v-for="(char, charKey) in characteristics" :key="charKey" :class="{ '<md:hidden': charKey }">
                - {{ char }}
              </li>
            </ul>
          </div>
          <!-- Long Intro -->
          <div
            v-if="renderStyle === 'longIntroduction'"
            class="mx-a px-6 pb-16 text-center text-sm <md:mt-16 md:w-1/2"
          >
            <div class="mb-6 ws-pre-wrap fw-bold">
              {{ title }}
            </div>
            <div class="ws-pre-wrap lh-4">
              {{ paragraph }}
            </div>
          </div>
          <!-- Short Intro -->
          <div v-if="renderStyle === 'shortIntroduction'" class="text-center <md:my-16" style="padding: 0 3.5rem">
            <div class="mb-6 ws-pre-wrap text-4xl fw-bold lh-6">
              {{ title }}
            </div>
            <div class="<md:text-lg <md:lh-6">
              {{ paragraph }}
            </div>
          </div>
          <!-- OverlaidTitle -->
          <div
            v-if="renderStyle === 'overlaidTitle'"
            class="absolute full flex center ws-pre-wrap text-center title-1 lh-4 md:lh-6"
          >
            {{ title }}
          </div>
          <!-- Images across all templates  -->
          <base-picture
            v-for="(image, i) in images"
            :key="i"
            v-bind="image"
            :class="{ '<md:hidden': i }"
          />
        </div>
      </base-grid>
    </section>
    <!-- Carousel -->
    <section class="mb-16 md:px-14">
      <base-carousel class="mb-16">
        <div
          v-for="({ image, title, paragraph, buttons }, key) in productsCarousel.products"
          :key
          class="w-2/3 snap-center px-6 md:w-1/4"
        >
          <base-picture v-bind="image" class="mb-4" height="747" width="790" />
          <div class="text-center" style="min-height:9rem">
            <h2 class="mb-3 text-2xl fw-bold">
              {{ title }}
            </h2>
            <p class="mb-3 md:text-sm">
              {{ paragraph }}
            </p>
          </div>
          <vf-button
            v-for="({ title: btnTitle, to }, k) in buttons"
            :key="k"
            class="mb-1 w-full uppercase"
            size="tiny"
            :to
            variant="primary-inverse"
          >
            {{ $t[btnTitle] }}
          </vf-button>
        </div>
      </base-carousel>
      <div class="mx-a px-6 text-center md:w-1/2">
        <h2 class="mb-4 text-3xl fw-bold">
          {{ productsCarousel.title }}
        </h2>
        <p>{{ productsCarousel.paragraph }}</p>
      </div>
    </section>
    <!-- Fixed black bottom icons bar -->
    <section class="fixed bottom-0 z-1 w-full flex center bg-black py-2">
      <base-link class="absolute left-0 m-a flex items-center <md:hidden" :to="allIconsBar.to">
        <vf-icon dir="left" name="chevron" size="md" />
        <span class="text-sm fw-bold uppercase">{{ allIconsBar.title }}</span>
      </base-link>
      <template v-for="({ title, to }, key) in allIconsBar.buttons" :key>
        <vf-button
          class="mx-2 uppercase"
          size="tiny"
          :to
          :variant="{ sm: 'primary', md: 'primary-inverse' }"
        >
          <span class="md:hidden">{{ title.sm }}</span>
          <span class="<md:hidden">{{ title.md }}</span>
        </vf-button>
        <span v-if="key < allIconsBar.buttons.length - 1" class="md:hidden">|</span>
      </template>
    </section>
  </main>
</template>

<script lang="ts" setup>
import type { PageConfig } from '#brand/types/pages/our-icons/denali'

definePageMeta({ header: { transparent: true } })

const activeGender = ref(0)
const { $t } = useNuxtApp()

const { data, error } = await useApi().internal.getStaticPage<PageConfig>('our-icons/denali')
if (!data.value) throw createError({ ...error.value!, fatal: true })
const { hero, chooseYourDenali, stories, productsCarousel, allIconsBar } = data.value
</script>
